import { Flex, Form } from '@/components';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { useCreateQueryStore } from '@/features/search/_hooks/query-store';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import {
  type ProjectsSearchSubmitType,
  validateAmountSearchCondition,
  validateProjectNumber,
} from '@/features/search/_utils/projects-search';
import { searchQueryKeyMap } from '@/features/search/_utils/query-keys';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { useInvalidateQuery, useIsFetching } from '@/hooks';
import { isPS } from '@/utils';
import isEqual from 'lodash-es/isEqual';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useProjectsSearchStore } from '../../_stores/projects-search-store';
import { AdditionalSearch } from './AdditionalSearch';
import { AmountSearch } from './AmountSearch';
import { BasicSearch } from './BasicSearch';
import { ProjectsSearchFieldTags } from './ProjectsSearchFieldTags';
import { SearchButton } from './SearchButton';
import { ActivityIndicatorSearch } from './fields/activity-indicator-search-fields/ActivityIndicatorSearch';

const FORM_ID = 'projects-segment-search-accordion';

type Props = {
  onSubmit: (values: ProjectsSearchSubmitType) => Promise<void>;
  onResetPayment: () => void;
  resetCount: number;
};
export const SearchForm: FC<Props> = ({
  onSubmit: _onSubmit,
  onResetPayment,
  resetCount,
}) => {
  const { t } = useTranslation();
  const {
    cascaderTargetIds: currentCascaderValues,
    inputCascaderAndAccountingLabels,
  } = useProjectsSearchStore();
  const { mutateAsync: createQueryStore } = useCreateQueryStore();
  const isFetching = useIsFetching({ queryKey: searchQueryKeyMap.projects() });
  const { invalidate } = useInvalidateQuery();
  const { searchQueryStates } = useProjectsPageSearchQueryStates();

  const {
    handleSubmit,
    formState: { isDirty },
    setError,
  } = useFormContext<ProjectSearchFormType>();

  const validateAnalysisAmount = useCallback(
    ({ formulaConditions }: ProjectSearchFormType) => {
      let hasError = false;
      if (!formulaConditions || formulaConditions.length === 0) {
        return { hasError };
      }

      for (const [index, condition] of formulaConditions.entries()) {
        const { hasNullField, hasMinMaxError } = validateAmountSearchCondition({
          formulaCondition: condition,
        });

        if (hasNullField) {
          setError(`formulaConditions.${index}.conditionType`, {
            message: t('validation.analysis-all-field-must-required'),
          });
          hasError = true;
        }

        if (hasMinMaxError) {
          setError(`formulaConditions.${index}.baseAmountValueFrom`, {
            message: t('validation.min-value-must-be-less-than-max'),
          });
          hasError = true;
        }
      }
      return { hasError };
    },
    [t, setError],
  );

  const onSubmit = useCallback(
    async (values: ProjectSearchFormType) => {
      const { hasError: hasProjectNumberError } = validateProjectNumber(
        values,
        setError,
      );
      const { hasError: hasAnalysisAmountError } =
        validateAnalysisAmount(values);

      if (hasProjectNumberError || hasAnalysisAmountError) {
        return;
      }
      const isRS = values.sheetType === 'RS';

      const newQueryStore = generateQueryAndLeafs({
        otherResponsibleOrganizationIds:
          values.nestedOtherResponsibleOrganizationIds,
        organizationIds: values.nestedOrganizationIds,
        policyIds: isRS ? values.nestedPolicyIds : null,
        expenditureBudgetPurposeIds: isRS
          ? values.nestedExpenditureBudgetPurposeIds
          : null,
      });

      const prevCascaderId = searchQueryStates.cascader;

      const hasChanged = Object.keys(currentCascaderValues).some((key) => {
        const newQuery = newQueryStore?.query[key];
        const currentCascaderValue = currentCascaderValues[key];
        return !isEqual(newQuery, currentCascaderValue);
      });

      const isDifferent =
        !isPS() &&
        hasChanged &&
        !isEqual(newQueryStore?.query, currentCascaderValues);

      const newCascadeId =
        isDifferent && newQueryStore
          ? (await createQueryStore(newQueryStore)).id
          : newQueryStore
            ? prevCascaderId
            : undefined;

      await _onSubmit({
        values,
        newCascadeId,
        inputCascaderAndAccountingLabels,
      });

      // // Note: If any field's value change then based on query params it will call api anyway, So use isDirty to avoid invalidate call
      if (!isFetching && !isDirty) {
        invalidate(searchQueryKeyMap.projects());
      }
    },
    [
      _onSubmit,
      currentCascaderValues,
      invalidate,
      isFetching,
      isDirty,
      createQueryStore,
      validateAnalysisAmount,
      searchQueryStates.cascader,
      inputCascaderAndAccountingLabels,
      setError,
    ],
  );

  return (
    <Form
      leavePageAlertCondition={null}
      id={FORM_ID}
      onFinish={handleSubmit(onSubmit)}
      layout="vertical"
    >
      <Flex vertical gap={16}>
        <BasicSearch resetCount={resetCount} onResetPayment={onResetPayment} />
        <ProjectsSearchFieldTags onResetPayment={onResetPayment} />
        <ActivityIndicatorSearch />
        <AdditionalSearch />
        <AmountSearch />
        <SearchButton formId={FORM_ID} onResetPayment={onResetPayment} />
      </Flex>
    </Form>
  );
};
